/**
 * useAccounts Hook
 *
 * React hook for account data with loading states.
 * Handles data fetching, caching, and refresh.
 */

import { useState, useEffect, useCallback } from 'react';
import { Account } from '@services/db';
import { accountService } from '@services/accounts';
import { dataEvents } from '@services/dataEvents';

interface UseAccountsResult {
  accounts: Account[];
  totalBalance: number;
  loading: boolean;
  error: string | null;
  refresh: () => Promise<void>;
}

/**
 * Hook to fetch and manage accounts data.
 */
export function useAccounts(): UseAccountsResult {
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [totalBalance, setTotalBalance] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchAccounts = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      // NOTE: Removed auto-recalculation - balances are set manually or updated when transactions change
      // This allows manual balance fixes to persist

      // Get all active accounts
      const data = await accountService.getActive();
      setAccounts(data);

      // Calculate total
      const total = data.reduce((sum, a) => sum + a.balance, 0);
      setTotalBalance(total);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to load accounts';
      setError(message);
      console.error('[useAccounts] Error:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAccounts();
  }, [fetchAccounts]);

  // Auto-refresh when accounts data changes (from transactions, sync, etc.)
  useEffect(() => {
    const unsubscribe = dataEvents.on('accounts', fetchAccounts);
    return unsubscribe;
  }, [fetchAccounts]);

  return {
    accounts,
    totalBalance,
    loading,
    error,
    refresh: fetchAccounts,
  };
}

/**
 * Hook to fetch a single account by ID.
 */
export function useAccount(id: string | undefined) {
  const [account, setAccount] = useState<Account | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!id) {
      setAccount(null);
      setLoading(false);
      return;
    }

    const fetchAccount = async () => {
      try {
        setLoading(true);
        setError(null);
        const data = await accountService.getById(id);
        setAccount(data || null);
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Failed to load account';
        setError(message);
      } finally {
        setLoading(false);
      }
    };

    fetchAccount();
  }, [id]);

  return { account, loading, error };
}
